import React, { useState, useEffect, useRef } from 'react';
import * as S from './widgets.styles';

interface LinkButtonTextProps {
  content: string;
  onAnimationComplete?: () => void;
  shouldStart?: boolean;
}

const LinkButtonText: React.FC<LinkButtonTextProps> = ({ content, onAnimationComplete, shouldStart = true }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onCompleteRef = useRef(onAnimationComplete);
  const previousContentRef = useRef<string>('');

  // Update callback ref when it changes
  useEffect(() => {
    onCompleteRef.current = onAnimationComplete;
  }, [onAnimationComplete]);

  useEffect(() => {
    // Clear any existing animation
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }

    // If no content, show immediately
    if (!content) {
      setDisplayedText('');
      setIsAnimating(false);
      previousContentRef.current = '';
      return;
    }

    // If animation disabled, show immediately and call completion
    if (!shouldStart) {
      setDisplayedText(content);
      setIsAnimating(false);
      previousContentRef.current = content;
      // Don't call completion here - let parent control when to show next button
      return;
    }

    // If content hasn't changed, don't restart animation
    if (content === previousContentRef.current) {
      return;
    }

    // New content - start animation
    previousContentRef.current = content;
    setDisplayedText('');
    setIsAnimating(true);

    let currentIndex = 0;
    animationIntervalRef.current = setInterval(() => {
      if (currentIndex < content.length) {
        setDisplayedText(content.substring(0, currentIndex + 1));
        currentIndex++;
      } else {
        // Animation complete
        if (animationIntervalRef.current) {
          clearInterval(animationIntervalRef.current);
          animationIntervalRef.current = null;
        }
        setIsAnimating(false);
        if (onCompleteRef.current) {
          onCompleteRef.current();
        }
      }
    }, 10); // 10ms per character for typing effect

    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
    };
  }, [content, shouldStart]);

  const text = isAnimating ? displayedText : content;
  const showCursor = isAnimating && displayedText.length < content.length;

  return (
    <>
      {text}
      {showCursor && <S.TypingCursor />}
    </>
  );
};

export default LinkButtonText;
